import { APP_INITIALIZER, Provider } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/**
 * App-wide whitespace trimming for text fields.
 *
 * When a text input / textarea loses focus (or Enter is pressed in an input), leading and
 * trailing spaces are removed and an `input` event is re-dispatched, so Angular's value
 * accessors (reactive forms and ngModel) receive the trimmed value and validators re-run.
 * A field holding only spaces therefore becomes empty and `required` fails as expected.
 *
 * Opt out on a specific field with the `data-no-trim` attribute.
 */

/** Input types whose values are free text and safe to trim. Passwords are deliberately excluded. */
const TRIMMABLE_INPUT_TYPES = new Set(['', 'text', 'email', 'search', 'tel', 'url']);

function isTrimmable(target: EventTarget | null): target is HTMLInputElement | HTMLTextAreaElement {
    if (target instanceof HTMLTextAreaElement) {
        return !target.readOnly && !target.disabled && !target.hasAttribute('data-no-trim');
    }
    if (!(target instanceof HTMLInputElement)) {
        return false;
    }
    if (target.readOnly || target.disabled || target.hasAttribute('data-no-trim')) {
        return false;
    }
    // ng-select's internal search box manages its own value.
    if (target.closest('ng-select, .ng-select')) {
        return false;
    }
    return TRIMMABLE_INPUT_TYPES.has((target.getAttribute('type') || '').toLowerCase());
}

function trimField(target: EventTarget | null): void {
    if (!isTrimmable(target)) {
        return;
    }
    const value = target.value;
    const trimmed = value.trim();
    if (trimmed === value) {
        return;
    }
    target.value = trimmed;
    target.dispatchEvent(new Event('input', { bubbles: true }));
}

export function registerGlobalInputTrim(doc: Document): () => void {
    return () => {
        // Capture phase: runs before the field's own blur handler, so `updateOn: 'blur'` controls
        // also commit the trimmed value.
        doc.addEventListener('blur', (event) => trimField(event.target), true);
        // Enter submits a form without blurring the field, so trim before the submit fires.
        doc.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
                trimField(event.target);
            }
        }, true);
    };
}

export const GLOBAL_INPUT_TRIM_PROVIDER: Provider = {
    provide: APP_INITIALIZER,
    useFactory: registerGlobalInputTrim,
    deps: [DOCUMENT],
    multi: true,
};
